import React, { useMemo } from 'react';

import type { PopperProps } from '@mui/material';
import { Autocomplete, Box, Checkbox, Chip, InputAdornment, Typography } from '@mui/material';
import clsx from 'clsx';
import { AlineAutocompleteInput, AlineAvatar, AlineTooltip } from 'src/components';
import { AddUserIcon } from 'src/components/icons/AddUserIcon';
import { UNASSIGNED_OPTION, UNASSIGNED_USER_ID } from 'src/components/reminders/constants';
import type { UserOption } from 'src/components/reminders/constants';
import { Apollo } from 'src/gql/generated';
import { useGetOrgMembers } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

export type { UserOption } from 'src/components/reminders/constants';

const ALWAYS_OPEN_LIST_HEIGHT = 240;

interface UserAutocompleteProps {
  onChange: (users: UserOption[]) => void;
  value: UserOption[];
  alwaysOpen?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  disablePortal?: boolean;
  fixedUserIds?: ReadonlySet<string>;
  includeUnassigned?: boolean;
  maxChips?: number;
  openOnFocus?: boolean;
  placeholder?: string;
}

/**
 * Renders children inline (normal document flow) instead of as a positioned Popper.
 * Used when `alwaysOpen` is true to keep the dropdown within the parent container.
 */
function StaticPopper({ children, className, open, style }: PopperProps): JSX.Element | null {
  if (!open) return null;
  return (
    <div
      className={className}
      style={{ ...style, inset: 'auto', position: 'relative', transform: 'none', width: '100%' }}
    >
      {typeof children === 'function' ? null : children}
    </div>
  );
}

const useStyles = makeStyles()(theme => ({
  alwaysOpenInput: {
    '&:focus-within': {
      border: 'unset',
      borderBottom: `1px solid ${theme.appColors.rule}`,
    },
    border: 'unset',
    borderBottom: `1px solid ${theme.appColors.rule}`,
    borderRadius: 0,
  },
  overflowBadge: {
    alignItems: 'center',
    backgroundColor: theme.appColors.primary,
    borderRadius: 12,
    color: theme.palette.common.white,
    display: 'flex',
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 700,
    height: 24,
    justifyContent: 'center',
    minWidth: 24,
    padding: theme.spacing(0, 0.5),
  },
  popupIcon: {
    color: theme.appColors.iconsType,
    margin: theme.spacing(0.5),
  },
}));

export function UserAutocomplete({
  alwaysOpen,
  autoFocus,
  disablePortal,
  disabled,
  fixedUserIds,
  includeUnassigned,
  maxChips,
  onChange,
  openOnFocus,
  placeholder = 'Select assignees',
  value,
}: UserAutocompleteProps): JSX.Element {
  const { classes } = useStyles();
  const visibleCount = maxChips ?? value.length;
  const { loading, orgMembers } = useGetOrgMembers({
    variables: { filter: Apollo.OrgMemberFilter.Active },
  });
  const options = useMemo(() => {
    const sorted = [...orgMembers].sort((a, b) => a.displayName.localeCompare(b.displayName));
    const all = includeUnassigned ? [UNASSIGNED_OPTION, ...sorted] : sorted;
    return fixedUserIds?.size ? all.filter(o => !fixedUserIds.has(o.id)) : all;
  }, [orgMembers, includeUnassigned, fixedUserIds]);

  return (
    <Autocomplete
      disableCloseOnSelect
      fullWidth
      multiple
      disablePortal={disablePortal}
      disabled={disabled}
      getOptionKey={option => option.id}
      getOptionLabel={option => option.displayName}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      loading={loading}
      open={alwaysOpen || undefined}
      openOnFocus={openOnFocus}
      options={options}
      popupIcon={alwaysOpen ? null : undefined}
      renderInput={params => (
        <AlineAutocompleteInput
          {...params}
          autoFocus={autoFocus}
          inputClassName={clsx(alwaysOpen && classes.alwaysOpenInput)}
          placeholder={value.length === 0 ? placeholder : undefined}
          size="small"
          startAdornment={
            <>
              <InputAdornment position="start">
                <AddUserIcon className={classes.popupIcon} />
              </InputAdornment>
              {params.slotProps.input.startAdornment}
            </>
          }
        />
      )}
      renderOption={({ key, ...props }, option, { selected }) => (
        <li key={key} {...props}>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
            <Checkbox checked={selected} size="small" sx={{ p: 0 }} />
            <AlineAvatar
              avatarInfo={option.id === UNASSIGNED_USER_ID ? null : option}
              styles={{ size: 'sm' }}
            />
            {option.displayName}
          </Box>
        </li>
      )}
      renderValue={(selected, getItemProps) => {
        const visible = selected.slice(0, visibleCount);
        const hiddenCount = selected.length - visibleCount;

        return (
          <>
            {visible.map((option, index) => {
              const { key, ...chipProps } = getItemProps({ index });
              const isFixed = fixedUserIds?.has(option.id);
              return (
                <AlineTooltip key={option.id} arrow placement="top" title={option.displayName}>
                  <Chip
                    {...chipProps}
                    avatar={
                      <AlineAvatar
                        avatarInfo={option.id === UNASSIGNED_USER_ID ? null : option}
                        styles={{ size: 'xs' }}
                      />
                    }
                    label=""
                    size="small"
                    sx={theme => ({
                      '& .MuiChip-avatar': {
                        color: theme.palette.common.white,
                        marginLeft: isFixed ? 0 : 0.5,
                        marginRight: isFixed ? 0 : 0,
                      },
                      '& .MuiChip-deleteIcon': { marginLeft: 0, marginRight: 0.5 },
                      '& .MuiChip-label': { display: 'none', padding: 0 },
                      backgroundColor: isFixed ? 'transparent' : theme.palette.grey[200],
                      ...(isFixed && { borderRadius: '50%', padding: 0 }),
                    })}
                    onDelete={isFixed ? undefined : chipProps.onDelete}
                  />
                </AlineTooltip>
              );
            })}
            {hiddenCount > 0 && <Typography>{`+${hiddenCount}`}</Typography>}
          </>
        );
      }}
      size="small"
      slotProps={
        alwaysOpen
          ? {
              listbox: { sx: { height: ALWAYS_OPEN_LIST_HEIGHT } },
              paper: {
                elevation: 0,
                sx: {
                  '& .MuiAutocomplete-noOptions': {
                    alignItems: 'center',
                    display: 'flex',
                    height: ALWAYS_OPEN_LIST_HEIGHT,
                    justifyContent: 'center',
                  },
                },
              },
            }
          : undefined
      }
      slots={alwaysOpen ? { popper: StaticPopper } : undefined}
      sx={{
        '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap', height: 40, overflow: 'hidden' },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderWidth: 1,
        },
      }}
      value={value}
      onChange={(_, newValue) => {
        if (!fixedUserIds?.size) return onChange(newValue);
        const fixed = value.filter(v => fixedUserIds.has(v.id));
        const nonFixed = newValue.filter(v => !fixedUserIds.has(v.id));
        onChange([...fixed, ...nonFixed]);
      }}
    />
  );
}
